import { createContext, useCallback, useContext, useEffect, useState, ReactNode } from "react";

export type FontScale = "s" | "m" | "l" | "xl";

const SCALE_FACTOR: Record<FontScale, number> = {
  s: 0.875,
  m: 1,
  l: 1.125,
  xl: 1.25,
};

// Wert + Schriftmuster. Die Beschriftung kommt aus dem Katalog
// (`settings.schriftgroesse[wert]`) — eine Tabelle kennt keine Sprache.
export const FONT_SCALE_OPTIONS: { value: FontScale; sample: string }[] = [
  { value: "s", sample: "14px" },
  { value: "m", sample: "16px" },
  { value: "l", sample: "18px" },
  { value: "xl", sample: "20px" },
];

interface Ctx {
  scale: FontScale;
  setScale: (s: FontScale) => void;
}

const FontScaleCtx = createContext<Ctx | null>(null);

function getInitial(): FontScale {
  const saved = localStorage.getItem("sb_font_scale");
  if (saved === "s" || saved === "m" || saved === "l" || saved === "xl") return saved;
  return "m";
}

function applyToRoot(scale: FontScale) {
  const factor = SCALE_FACTOR[scale];
  document.documentElement.style.setProperty("--font-scale", String(factor));
}

export function FontScaleProvider({ children }: { children: ReactNode }) {
  const [scale, setScaleState] = useState<FontScale>(getInitial);

  useEffect(() => {
    applyToRoot(scale);
    localStorage.setItem("sb_font_scale", scale);
  }, [scale]);

  const setScale = useCallback((s: FontScale) => setScaleState(s), []);

  return <FontScaleCtx.Provider value={{ scale, setScale }}>{children}</FontScaleCtx.Provider>;
}

export function useFontScale(): Ctx {
  const v = useContext(FontScaleCtx);
  if (!v) throw new Error("useFontScale outside FontScaleProvider");
  return v;
}
